import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { DebugOverlay } from '../../debug';

// Phone-side pairing page used after scanning the QR code shown on a monitor.
// If no `code` param is present, admins can enter the pairing code manually.
export default function KioskPairPage() {
  const [searchParams] = useSearchParams();
  const initialCode = (searchParams.get('code') || '').trim();

  const [codeInput, setCodeInput] = useState(initialCode);
  const [activeCode, setActiveCode] = useState(initialCode);

  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'pending' | 'completed' | 'expired' | 'error'
  const [statusData, setStatusData] = useState(null);
  const [statusError, setStatusError] = useState(null);

  const [monitorName, setMonitorName] = useState('');
  const [selectedView, setSelectedView] = useState('substitution');
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [scannerActive, setScannerActive] = useState(false);
  const [scannerSupported, setScannerSupported] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const supported = !!(navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    setScannerSupported(supported);
  }, []);

  // Load pairing status whenever we have an active code (from URL or manual input).
  useEffect(() => {
    if (!activeCode) return;

    let cancelled = false;

    const loadStatus = async () => {
      try {
        setStatus('loading');
        setStatusError(null);

        // Calls PostgreSQL function public.fn_monitor_get_pairing_status(p_code text)
        const { data, error } = await supabase.rpc('fn_monitor_get_pairing_status', {
          p_code: activeCode
        });

        if (cancelled) return;

        if (error) {
          console.error('Error loading monitor pairing status:', error);
          setStatus('error');
          setStatusError('Fehler beim Laden des Pairing-Status. Bitte versuchen Sie es später erneut.');
          setStatusData(null);
          return;
        }

        const statusValue = data && data.status ? data.status : 'error';

        if (statusValue === 'pending' || statusValue === 'completed' || statusValue === 'expired') {
          setStatus(statusValue);
          setStatusData(data || null);
        } else {
          setStatus('error');
          setStatusError('Ungültige Antwort vom Server für diesen Pairing-Code.');
          setStatusData(null);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Unexpected error loading pairing status:', e);
        setStatus('error');
        setStatusError('Unerwarteter Fehler beim Laden des Pairing-Status.');
        setStatusData(null);
      }
    };

    loadStatus();

    return () => {
      cancelled = true;
    };
  }, [activeCode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!scannerActive) return;

    let cancelled = false;
    let animationFrameId = null;

    const startScanner = async () => {
      try {
        setScannerError(null);

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setScannerError('Camera access is not supported on this device. Please enter the code manually.');
          setScannerActive(false);
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (!videoRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        try {
          await videoRef.current.play();
        } catch { }

        const Detector = window.BarcodeDetector;
        if (!Detector) {
          setScannerError('QR scanning is not supported in this browser. Please enter the code manually.');
          return;
        }
        const detector = new Detector({ formats: ['qr_code'] });

        const scan = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes && barcodes.length > 0) {
              const value = barcodes[0].rawValue || '';
              let extracted = value;
              try {
                const url = new URL(value);
                extracted = url.searchParams.get('code') || value;
              } catch { }
              const trimmed = (extracted || '').trim().toUpperCase();
              if (trimmed) {
                setCodeInput(trimmed);
                setActiveCode(trimmed);
                setScannerActive(false);
                return;
              }
            }
          } catch (err) {
            console.error('Error while scanning QR code:', err);
          }
          animationFrameId = window.requestAnimationFrame(scan);
        };

        animationFrameId = window.requestAnimationFrame(scan);
      } catch (err) {
        console.error('Error accessing camera for QR scan:', err);
        setScannerError('Kamera konnte nicht verwendet werden. Bitte prüfen Sie die Berechtigungen oder geben Sie den Code manuell ein.');
        setScannerActive(false);
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [scannerActive]);

  // Allow manual code entry when no `code` query param is present.
  const handleCheckCode = (e) => {
    if (e) e.preventDefault();
    const trimmed = (codeInput || '').trim().toUpperCase();
    if (!trimmed) {
      setStatus('error');
      setStatusError('Bitte einen gültigen Pairing-Code eingeben.');
      setStatusData(null);
      return;
    }
    setActiveCode(trimmed);
    setSubmitSuccess(null);
    setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!activeCode) {
      setSubmitError('Kein gültiger Pairing-Code vorhanden.');
      return;
    }

    if (!monitorName.trim()) {
      setSubmitError('Bitte einen Namen für den Monitor angeben.');
      return;
    }

    try {
      setSubmitting(true);

      const { data, error } = await supabase.rpc('fn_monitor_complete_pairing', {
        p_pairing_code: activeCode,
        p_monitor_name: monitorName.trim(),
        p_allowed_views: [selectedView]
      });

      if (error) {
        console.error('Error submitting monitor pairing via RPC:', error);
        throw new Error('Serverfehler beim Verbinden des Monitors.');
      }

      if (!data || typeof data.status !== 'string') {
        throw new Error('Ungültige Antwort vom Server beim Verbinden des Monitors.');
      }

      if (data.status === 'error') {
        if (data.code === 'pairing_invalid_or_expired') {
          throw new Error('Dieser Pairing-Code ist ungültig oder abgelaufen. Bitte einen neuen Code vom Monitor verwenden.');
        }
        if (data.code === 'no_school_context') {
          throw new Error('Es konnte keine Schulzuordnung ermittelt werden. Bitte melden Sie sich erneut an.');
        }
        const message = data.message || data.error || 'Serverfehler beim Verbinden des Monitors.';
        throw new Error(message);
      }

      setSubmitSuccess({ monitorName: monitorName.trim(), view: selectedView });
      // After a successful submit we treat the pairing as completed for the UI.
      setStatus('completed');
    } catch (err) {
      console.error('Error submitting monitor pairing:', err);
      setSubmitError((err && err.message) || 'Unbekannter Fehler beim Verbinden des Monitors.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusMessage = () => {
    if (!activeCode) return null;

    if (status === 'loading') {
      return <p className="mt-2 text-sm text-gray-500">Lade Pairing-Status…</p>;
    }

    if (status === 'expired') {
      return (
        <p className="mt-2 text-sm text-red-600">
          Dieser Code ist abgelaufen. Bitte lassen Sie auf dem Monitor einen neuen QR-Code anzeigen.
        </p>
      );
    }

    if (status === 'completed') {
      return (
        <p className="mt-2 text-sm text-green-700">
          Dieser Code wurde bereits verwendet. Sie können die Einstellungen aktualisieren.
        </p>
      );
    }

    if (status === 'pending') {
      return (
        <p className="mt-2 text-sm text-gray-600">
          Dieser Monitor wartet auf Bestätigung. Sie können ihn jetzt konfigurieren.
        </p>
      );
    }

    if (status === 'error' && statusError) {
      return (
        <p className="mt-2 text-sm text-red-600">
          {statusError}
        </p>
      );
    }

    return null;
  };

  const anyStatusError = status === 'error' && statusError;

  return (
    <DebugOverlay name="KioskPairPage">
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Monitor verbinden</h1>
          <p className="text-sm text-gray-600 mb-4">
            Hier können Sie einen Monitor mit einem QR-Code koppeln. Den Pairing-Code sehen Sie direkt auf dem Monitor.
          </p>

          {/* Manual code entry when no code query param is present (or to override). */}
          {!initialCode && (
            <div className="mb-4 space-y-4">
              <form onSubmit={handleCheckCode} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700" htmlFor="pairing-code-input">
                  Pairing-Code eingeben
                </label>
                <div className="flex gap-2">
                  <input
                    id="pairing-code-input"
                    type="text"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="z. B. K7P3FQ"
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Code prüfen
                  </button>
                </div>
              </form>

              <div className="border-t border-gray-100 pt-3">
                {scannerSupported ? (
                  <div className="space-y-2 text-left">
                    <button
                      type="button"
                      onClick={() => setScannerActive((prev) => !prev)}
                      className="inline-flex items-center justify-center rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {scannerActive ? 'Kamera stoppen' : 'Kamera zum Scannen verwenden'}
                    </button>
                    {scannerError && (
                      <p className="text-xs text-red-600">{scannerError}</p>
                    )}
                    {!scannerError && (
                      <p className="text-xs text-gray-500">
                        Optional: Verwenden Sie die Kamera Ihres Geräts, um den QR-Code auf dem Monitor automatisch zu scannen.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 text-left">
                    Kamera-Scanning wird von diesem Browser nicht unterstützt. Sie können den Code oben manuell eingeben.
                  </p>
                )}
              </div>

              {scannerActive && (
                <div className="overflow-hidden rounded-md border border-gray-200">
                  <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600 text-left">
                    Richten Sie die Kamera auf den QR-Code auf dem Monitor. Der Code wird automatisch übernommen.
                  </div>
                  <div className="bg-black">
                    <video
                      ref={videoRef}
                      className="w-full h-56 object-cover"
                      playsInline
                      muted
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeCode && (
            <div className="mt-2">
              <div className="text-sm text-gray-600">Aktueller Pairing-Code</div>
              <div className="mt-1 text-2xl font-mono tracking-[0.35em] text-gray-900">
                {activeCode}
              </div>
              {renderStatusMessage()}
            </div>
          )}

          {anyStatusError && !activeCode && (
            <p className="mt-2 text-sm text-red-600">{statusError}</p>
          )}

          {activeCode && (status === 'pending' || status === 'completed') && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="monitor-name">
                  Monitor-Name
                </label>
                <input
                  id="monitor-name"
                  type="text"
                  required
                  value={monitorName}
                  onChange={(e) => setMonitorName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="z. B. Foyer Monitor, Sekretariat TV"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700">Ansicht auswählen</span>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="view"
                      value="substitution"
                      checked={selectedView === 'substitution'}
                      onChange={() => setSelectedView('substitution')}
                      className="h-4 w-4"
                    />
                    <span>Vertretungsplan</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="view"
                      value="flex_overview"
                      checked={selectedView === 'flex_overview'}
                      onChange={() => setSelectedView('flex_overview')}
                      className="h-4 w-4"
                    />
                    <span>Flex/AG Übersicht</span>
                  </label>
                </div>
              </div>

              {submitError && (
                <p className="text-sm text-red-600">{submitError}</p>
              )}

              {submitSuccess && (
                <div className="text-sm text-green-700 border border-green-200 bg-green-50 rounded-md px-3 py-2">
                  <p className="font-medium mb-1">Monitor erfolgreich verbunden.</p>
                  <p className="mb-1">Sie können jetzt zum Monitor zurückkehren.</p>
                  <p className="text-xs text-green-800">
                    Name: {submitSuccess.monitorName} • Ansicht: {submitSuccess.view === 'substitution' ? 'Vertretungsplan' : 'FlexBand Übersicht'}
                  </p>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Monitor wird verbunden…' : 'Monitor verbinden'}
                </button>
              </div>
            </form>
          )}

          {activeCode && status === 'expired' && (
            <p className="mt-6 text-sm text-gray-600">
              Dieser Pairing-Code kann nicht mehr verwendet werden. Bitte zeigen Sie auf dem Monitor einen neuen QR-Code an
              und geben Sie den neuen Code hier ein.
            </p>
          )}
        </div>
      </div>
    </DebugOverlay>
  );
}
